import { batch } from 'react-redux';
import { MEETING_ENDED_MODAL } from '@shoflo/polymer';
import { getBroadcastRouteMatch } from 'actions/utils';
import { isValidLayout } from 'utils/layoutHelper';
import {
  syncSources,
  stopUILoading,
  startUILoading,
  syncLayerSources,
  cancelDisconnectSource,
  updateBackgroundUploadStatus,
  updateOverlayUploadStatus,
  createContentSourceStatus,
  updateContentSourceStatus
} from 'actions/Broadcast';

import {
  BROADCAST_SCENE_UPDATE,
  BROADCAST_SCENES_UPDATE,
  BROADCAST_UPDATE_STREAM_REALTIME,
  BROADCAST_UPDATE_RECORDING_REALTIME,
  UPDATE_SCENE_SPINNER,
  BROADCAST_UPDATE_SOURCE,
  BROADCAST_DELETE_BACKGROUND,
  BROADCAST_DELETE_OVERLAY,
  BROADCAST_DELETE_SOURCE,
  BROADCAST_DELETE_LIVE_RECORDING
} from 'actions/Types';
import { sendToDatadogWithIds } from '@shoflo/studio-redux/utils';
import {
  updateSourceTranscodingStatus,
  setSourceUploadStatus,
  showModal,
  showNotification
} from '@shoflo/studio-redux/actions';
import { STOPPED_BY_USER } from '@shoflo/studio-redux/constants/StreamStoppedReasons';
import { FAILED } from 'constants/SourceUploadStatus';
import { logout } from 'actions/User/LogoutUser';
import { HOST_PERMISSION_CHANGED } from '@studio/constants/ModalTypes';
import {
  s3ProxyUploadStatuses,
  TranscodingStatuses
} from '@studio/constants/MediaStatus';

export function processIncomingRealtimeMessage() {
  const store = this.store;
  const payload = this.payload;

  const snsMessage = parseIfJSON(payload);

  const { params } = getBroadcastRouteMatch(store.getState);
  let {
    broadcastStreamEndpoints,
    backgrounds,
    overlays,
    broadcastSources,
    user
  } = store.getState();

  const {
    params: { broadcastId, eventId, eventDocId }
  } = snsMessage;

  const isCurrentEvent = params.eventId === eventId;

  const clientMethod = snsMessage.tags.find(tag => tag.includes('client:'));

  /** Listen for eventDoc sns logs */
  if (isCurrentEvent) {
    if (isValidClientMethod(clientMethod)) {
      if (
        'client:broadcastRole.updateBroadcastRoles' === clientMethod &&
        snsMessage.responsePayload?.userId === user._id &&
        snsMessage.responsePayload?.broadcastId === params.broadcastId &&
        snsMessage.responsePayload?.role === 'presenter'
      ) {
        store.dispatch(showModal(HOST_PERMISSION_CHANGED));
        store.dispatch(logout());
      } else if (
        'client:broadcastRole.deleteBroadcastRole' === clientMethod &&
        user._id === snsMessage.responsePayload?.userId &&
        snsMessage.responsePayload?.broadcastId === params.broadcastId
      ) {
        store.dispatch(showModal(HOST_PERMISSION_CHANGED));
        store.dispatch(logout());
      } else if ('client:eventDoc.updateTranscodingStatus' === clientMethod) {
        const { responsePayload } = snsMessage;

        const payload = {
          eventDocId,
          transcodingStatus: responsePayload?.transcodingStatus
        };
        store.dispatch(updateSourceTranscodingStatus(payload));
      } else if ('client:eventDoc.updateUploadStatus' === clientMethod) {
        // Try matching the returned eventDocId with backgrounds, followed by overlays, followed by broadcastSources
        let entity;
        if ((entity = matchEventDocId(backgrounds, eventDocId))) {
          // Update the states of the background and dispatch updates
          if (
            snsMessage.payload &&
            snsMessage.payload.status === 'SCAN_SUCCESS' &&
            snsMessage.payload.location
          ) {
            store.dispatch(updateBackgroundUploadStatus(entity));
            sendToDatadogWithIds(
              store.getState,
              'studio.s3Proxy.upload',
              `Background Uploaded Successfully with eventDocId: ${eventDocId}`,
              false
            );
          }
          if (snsMessage.payload.status === 'SCAN_FAILED') {
            store.dispatch({
              type: `${BROADCAST_DELETE_BACKGROUND}_SUCCESS`,
              payload: entity._id
            });
            sendToDatadogWithIds(
              store.getState,
              'studio.s3Proxy.upload',
              `Background Upload failed for eventDocId: ${eventDocId}`,
              true
            );
          }
          return;
        }

        if ((entity = matchEventDocId(overlays, eventDocId))) {
          // Update the states of the overlay and dispatch action updates
          if (
            snsMessage.payload &&
            snsMessage.payload.status === 'SCAN_SUCCESS' &&
            snsMessage.payload.location
          ) {
            store.dispatch(updateOverlayUploadStatus(entity));
            sendToDatadogWithIds(
              store.getState,
              'studio.s3Proxy.upload',
              `Overlay Uploaded Successfully with eventDocId: ${eventDocId}`,
              false
            );
          }
          if (snsMessage.payload.status === 'SCAN_FAILED') {
            store.dispatch({
              type: `${BROADCAST_DELETE_OVERLAY}_SUCCESS`,
              payload: entity._id
            });
            sendToDatadogWithIds(
              store.getState,
              'studio.s3Proxy.upload',
              `Overlay Upload failed for eventDocId: ${eventDocId}`,
              true
            );
          }
          return;
        }

        if ((entity = createdBroadcastSource(broadcastSources, eventDocId))) {
          // Update the states of the broadcast sources and dispatch action updates
          if (
            snsMessage.payload &&
            snsMessage.payload.status === 'SCAN_SUCCESS' &&
            snsMessage.payload.location
          ) {
            const { isUpdating, isCreating } = broadcastSources;
            if (isCreating) {
              store.dispatch(createContentSourceStatus(entity));
            } else if (isUpdating) {
              store.dispatch(updateContentSourceStatus(entity));
            }
            sendToDatadogWithIds(
              store.getState,
              'studio.s3Proxy.upload',
              `Source Uploaded Successfully with eventDocId: ${eventDocId}`,
              false
            );
          } else if (snsMessage.payload.status === 'SCAN_FAILED') {
            store.dispatch(setSourceUploadStatus(entity.title, FAILED));
            store.dispatch({
              type: `${BROADCAST_DELETE_SOURCE}_SUCCESS`,
              payload: {
                _id: entity._id
              }
            });
            sendToDatadogWithIds(
              store.getState,
              'studio.s3Proxy.upload',
              `Source Upload Failed for eventDocId: ${eventDocId}`,
              true
            );
          }
          return;
        }
      } else if ('client:eventDoc.updateMediaStatus' === clientMethod) {
        handleUpdateMediaStatus(
          snsMessage,
          broadcastSources,
          eventDocId,
          store.dispatch,
          store.getState
        );
      }
    }
  }

  if (params.broadcastId === broadcastId) {
    /*
      will match the api request `broadcast.updateScene`, 'broadcast.createScene`,etc.
      We only want to dispatch this for the following requests:
      `broadcast.updateRecordingTaskState`,
      `broadcast.updateStreamEndpointTaskState` and `broadcast.updateScene`
    */
    if (isValidClientMethod(clientMethod)) {
      if (clientMethod === 'client:broadcast.stopStreams') {
        const {
          payload: { endpoints }
        } = snsMessage;
        const stoppedEndpoints = broadcastStreamEndpoints
          .filter(endpoint => endpoints.includes(endpoint._id))
          .map(ep => {
            return {
              ...ep,
              canStartStream: false
            };
          });

        batch(() => {
          for (const stoppedEndpoint of stoppedEndpoints) {
            store.dispatch({
              type: `${BROADCAST_UPDATE_STREAM_REALTIME}`,
              payload: {
                ...stoppedEndpoint
              }
            });
          }
        });
      }

      /** handle updates to recording and streaming task state */
      if (
        [
          'client:broadcast.updateStreamEndpointTaskState',
          'client:broadcast.updateRecordingTaskState'
        ].includes(clientMethod)
      ) {
        const { payload, responsePayload } = snsMessage;
        if (payload.taskState) {
          if (responsePayload?.endpoint) {
            store.dispatch({
              type: `${BROADCAST_UPDATE_STREAM_REALTIME}`,
              payload: {
                ...responsePayload.endpoint,
                canStartStream: payload.taskState === 'STOPPED'
              }
            });

            if (
              payload.taskState === 'STOPPED' &&
              payload.stoppedReason &&
              payload.stoppedReason !== STOPPED_BY_USER
            ) {
              store.dispatch(
                showNotification(
                  'A stream has been interrupted. Please restart the stream to reach your audience',
                  {
                    timeout: 5000,
                    type: 'alert'
                  }
                )
              );
            }
          } else {
            const { recording, baseRecording, deletedRecordingId } =
              responsePayload || {};

            if (recording) {
              store.dispatch({
                type: `${BROADCAST_UPDATE_RECORDING_REALTIME}`,
                payload: recording
              });
            }

            if (baseRecording) {
              store.dispatch({
                type: `${BROADCAST_UPDATE_RECORDING_REALTIME}`,
                payload: baseRecording
              });
            }

            if (deletedRecordingId) {
              store.dispatch({
                type: `${BROADCAST_DELETE_LIVE_RECORDING}`,
                payload: { deletedRecordingId }
              });
            }
          }
        }
      }

      /** handle createScene, updateScene + updateScenes */
      if (
        [
          'client:broadcast.updateScene',
          'client:broadcast.createScene'
        ].includes(clientMethod)
      ) {
        const { payload, responsePayload } = snsMessage;

        /**
         * Start thumbnail loading for a scene when result from realtime
         * payload does not have a thumbnail property
         */
        if (!payload.thumbnail) {
          const sceneId = responsePayload.scene._id;
          store.dispatch(startUILoading(`${UPDATE_SCENE_SPINNER}_${sceneId}`));
        }

        /**
         * For updateScene and createScene,
         * responsePayload will have a 'scene' property
         */
        store.dispatch({
          type: `${BROADCAST_SCENE_UPDATE}_SUCCESS`,
          payload: {
            scene: {
              ...responsePayload.scene,
              thumbnail: payload.thumbnail
            }
          }
        });

        if (payload.thumbnail) {
          store.dispatch(
            stopUILoading(
              `${UPDATE_SCENE_SPINNER}_${responsePayload.scene._id}`
            )
          );
        }
      }

      if (
        [
          'client:broadcast.updateScenes',
          'client:broadcast.updateScenesSourceLayer'
        ].includes(clientMethod)
      ) {
        const { payload, responsePayload } = snsMessage;

        //Start thumbnail loading for scenes
        if (payload.scenes && payload.scenes.length) {
          const sceneIds = payload.scenes
            ? payload.scenes.map(s => s._id)
            : [responsePayload.scene._id];
          batch(() => {
            for (const sceneId of sceneIds) {
              store.dispatch(
                startUILoading(`${UPDATE_SCENE_SPINNER}_${sceneId}`)
              );
            }
          });
        }

        //responsePayload will have a 'scenes' property instead of 'scene'
        store.dispatch({
          type: `${BROADCAST_SCENES_UPDATE}_SUCCESS`,
          payload: {
            scenes: responsePayload.scenes
          }
        });

        payload.scenes.forEach(scene => {
          if (scene.thumbnail) {
            store.dispatch(
              stopUILoading(`${UPDATE_SCENE_SPINNER}_${scene._id}`)
            );
          }
        });
      }

      /** handle update program */
      if (clientMethod.includes('updateProgram')) {
        const { responsePayload: broadcast } = snsMessage;
        const {
          program: { _id }
        } = broadcast;
        // programs from scenes have a mongoId thus not valid layout id
        if (!isValidLayout(_id)) {
          // will sync broadcast sources
          store.dispatch(syncSources());
          // will sync layer sources - banners, presenterBanners, overlays, backgrounds, globalSceneLayers
          store.dispatch(syncLayerSources(broadcast));
        }
      }

      /** handle update Source by attendeeId when attendee leaves meeting */
      if (clientMethod.includes('updateSourceByAttendeeId')) {
        const { responsePayload: source } = snsMessage;
        // Update reducer with new source
        store.dispatch({
          type: `${BROADCAST_UPDATE_SOURCE}_SUCCESS`,
          payload: {
            ...source
          }
        });
        // Cancel disconnect state
        if (source) {
          store.dispatch(cancelDisconnectSource(source._id));
        }
      }

      /**
       * dispatch the `showModal` event if it is deleted
       */
      if (clientMethod === 'client:broadcast.deleteMeeting') {
        console.info('Meeting ended via realtime message');
        store.dispatch(showModal(MEETING_ENDED_MODAL));
      }
    }
  }
}

// TODO: consolidate this function in WEBINAR-38206
function handleUpdateMediaStatus(
  snsMessage,
  broadcastSources,
  eventDocId,
  dispatch,
  getState
) {
  const { payload, responsePayload } = snsMessage;
  let entity;

  // Try matching the returned eventDocId with broadcastSources
  if ((entity = createdBroadcastSource(broadcastSources, eventDocId))) {
    if (payload?.status === s3ProxyUploadStatuses.ERROR) {
      if (responsePayload?.transcodingStatus) {
        const transcodePayload = {
          eventDocId,
          transcodingStatus: TranscodingStatuses.FAILED
        };
        sendToDatadogWithIds(
          getState,
          'studio.uvs.upload',
          `Source transcoding failed for eventDocId: ${eventDocId}`,
          true
        );
        dispatch(updateSourceTranscodingStatus(transcodePayload));

        return;
      }
    }

    if (Object.values(s3ProxyUploadStatuses).includes(payload?.status)) {
      if (payload.status === s3ProxyUploadStatuses.SCAN_SUCCESS) {
        const { isUpdating, isCreating } = broadcastSources;
        if (isCreating) {
          dispatch(createContentSourceStatus(entity));
        } else if (isUpdating) {
          dispatch(updateContentSourceStatus(entity));
        }
        sendToDatadogWithIds(
          getState,
          'studio.uvs.upload',
          `Source Uploaded Successfully with eventDocId: ${eventDocId}`,
          false
        );
      } else if (
        payload.status === s3ProxyUploadStatuses.SCAN_FAILED ||
        payload.status === s3ProxyUploadStatuses.ERROR
      ) {
        dispatch(setSourceUploadStatus(entity.title, FAILED));
        dispatch({
          type: `${BROADCAST_DELETE_SOURCE}_SUCCESS`,
          payload: {
            _id: entity._id
          }
        });
        sendToDatadogWithIds(
          getState,
          'studio.uvs.upload',
          `Source Upload Failed for eventDocId: ${eventDocId}`,
          true
        );
      }
    } else if (
      Object.values(TranscodingStatuses).includes(payload?.status) &&
      responsePayload?.transcodingStatus
    ) {
      const transcodePayload = {
        eventDocId,
        transcodingStatus: responsePayload.transcodingStatus
      };
      dispatch(updateSourceTranscodingStatus(transcodePayload));
    }
  }
}

function matchEventDocId(sourcesData, eventDocId) {
  return sourcesData.find(sourceData => sourceData.source.value === eventDocId);
}

function createdBroadcastSource(broadcastSources, eventDocId) {
  return broadcastSources.sources.find(
    broadcastSource =>
      broadcastSource.value.eventDocId === eventDocId ||
      broadcastSource.value === eventDocId
  );
}

function isValidClientMethod(clientMethod) {
  return clientMethod && typeof clientMethod === 'string';
}

function parseIfJSON(message) {
  try {
    // sns messages are delivered as json
    return JSON.parse(message);
  } catch (err) {
    // direct realtime messages are sent as a plain js object
    return message;
  }
}
